import React, {ReactNode, useLayoutEffect, useRef} from 'react';
import classNames from 'classnames';

import {StarIconFilled} from '../icons/StarIconFilled';
import {ExpandIcon} from '../icons/ExpandIcon';

import {ItemState} from '../Tree/types';

import './ItemLine.css';

const ICON_SIZE = 16;

export interface ItemLinePureProps {
    id: string;
    /** Pre-resolved item name (may be HTML-encoded highlight or plain) */
    name: string;
    isCluster: boolean;
    isExpanded: boolean;
    isPinned: boolean;
    isSelected: boolean;
    isReadOnly: boolean;
    isInSearch: boolean;
    level: number;
    paddingLeft: number;
    state?: ItemState;
    inEditorView?: boolean;
    subtitle?: string | null;
    showConfirmDelete: boolean;
    scrollIntoView: boolean;
    /** True while the "more" context menu is open — keeps button row visible */
    isMoreClicked?: boolean;
    parentsHash?: string | null;
    // callbacks
    onSelect: () => void;
    onExpandToggle: (e: React.SyntheticEvent) => void;
    onPinToggle: (e: React.SyntheticEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
    // slots for store-bound children
    buttonsSlot?: ReactNode;
    activeUsersSlot?: ReactNode;
    searchContextSlot?: ReactNode;
    confirmDeleteSlot?: ReactNode;
}

export const ItemLinePure = ({
    id,
    name,
    isCluster,
    isExpanded,
    isPinned,
    isSelected,
    isReadOnly,
    isInSearch,
    level,
    paddingLeft,
    state = 'idle',
    inEditorView = false,
    subtitle,
    showConfirmDelete,
    scrollIntoView,
    isMoreClicked = false,
    parentsHash,
    onSelect,
    onExpandToggle,
    onPinToggle,
    onContextMenu,
    buttonsSlot,
    activeUsersSlot,
    searchContextSlot,
    confirmDeleteSlot,
}: ItemLinePureProps) => {
    const itemLineRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        if (scrollIntoView && itemLineRef.current) {
            itemLineRef.current.scrollIntoView();
        }
    }, [scrollIntoView]);

    const showExtendIcon = isCluster && !isInSearch;
    const statusSubtitle = typeof subtitle === 'string' && subtitle.trim().length > 0 ? subtitle : null;
    const showStatusSubtitle = !!statusSubtitle && !isInSearch;

    return (
        <>
            <div
                ref={itemLineRef}
                className={classNames({
                    'ItemLine title-small': true,
                    'ItemLine--inEditorView': inEditorView,
                    'ItemLine--selected': isSelected,
                    'ItemLine--search': isInSearch,
                    'ItemLine--dragging': state === 'dragging',
                    'ItemLine--drag-over': state === 'parent-of-instruction',
                    'ItemLine--confirm-delete': showConfirmDelete,
                    'ItemLine--isCluster': isCluster,
                    'ItemLine--withSubtitle': showStatusSubtitle,
                })}
                onClick={onSelect}
                onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isReadOnly) {
                        onContextMenu(e);
                    }
                }}
            >
                <div
                    className={classNames({
                        'ItemLine__row': true,
                        'ItemLine__row-Reader': isReadOnly,
                        'ItemLine__row-moreClicked': isMoreClicked,
                    })}
                    style={{paddingLeft}}
                >
                    <div className="ItemLine__block">
                        <div className="ItemLine__block-main">
                            {showExtendIcon ? (
                                <ExpandIcon
                                    onClick={onExpandToggle}
                                    size={ICON_SIZE}
                                    isExpanded={isExpanded}
                                />
                            ) : null}
                            {isPinned ? (
                                <StarIconFilled
                                    className="ItemLine_pin"
                                    onClick={onPinToggle}
                                    size={ICON_SIZE}
                                />
                            ) : null}
                            <span
                                className="ItemLine__text"
                                dangerouslySetInnerHTML={{__html: name}}
                            />
                        </div>
                        {showStatusSubtitle ? (
                            <div
                                className={classNames({
                                    'ItemLine__subtitle label-small': true,
                                    'ItemLine__subtitle--withExpand': showExtendIcon,
                                })}
                                title={statusSubtitle}
                            >
                                {statusSubtitle}
                            </div>
                        ) : null}
                        {searchContextSlot ?? null}
                    </div>
                    {buttonsSlot ?? null}
                    {activeUsersSlot ?? null}
                </div>
            </div>
            <div>
                {showConfirmDelete ? confirmDeleteSlot ?? null : null}
            </div>
        </>
    );
};
